/* Khách đang tư vấn — mọi khách hôm nay theo bước đang làm; bấm khách để mở hồ sơ và làm tiếp đúng bước */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Switch } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { Search } from 'lucide-react';
import { STEPS, calc, curStep, hm, isToday, paidOf, stepDone, vnd, type CurStep, type Visit } from './logic';
import { useTuVan } from './store';
import { Card, RTable, Shell, SourceTag, StatusTag, useDoctorView, useMe, useNow, who } from './shared';

const STEP_LABEL: Record<CurStep, string> = {
  wait: 'Chờ nhận', consult: 'Phiếu tư vấn', exam: 'Bác sĩ khám', quote: 'Báo giá', deposit: 'Cọc · thanh toán', result: 'Kết quả', exec: 'Chuyển thực hiện', done: 'Đã xong',
};
const FILTERS: CurStep[] = ['consult', 'exam', 'quote', 'deposit', 'result', 'exec', 'done', 'wait'];

/** 5 chấm tiến độ: xanh = xong, viền = đang làm */
const Dots = ({ v, cur }: { v: Visit; cur: CurStep }) => (
  <span className="flex items-center gap-1">
    {STEPS.map((s) => (
      <span key={s.key} title={s.label}
        className={`h-2 w-2 rounded-full ${stepDone(v, s.key) ? 'bg-green-500' : s.key === cur ? 'bg-white ring-2 ring-[#3e91ab] dark:bg-gray-800' : 'bg-gray-200 dark:bg-gray-600'}`} />
    ))}
  </span>
);

export default function KhachTuVanPage() {
  const navigate = useNavigate();
  const now = useNow();
  const me = useMe();
  const doctor = useDoctorView();
  const { visits, branchId } = useTuVan();
  const [filter, setFilter] = useState<CurStep | 'all'>('all');
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState('');

  const today = useMemo(() => visits
    .filter((v) => v.branchId === branchId && v.status !== 'referral' && v.status !== 'cancelled' && !(v.queue === 'exec' && !v.consultant) && isToday(v.checkInAt ?? v.createdAt, now))
    .map((v) => ({ v, cur: curStep(v) })), [visits, branchId, now]);
  const count = (s: CurStep) => today.filter((x) => x.cur === s).length;
  const kw = q.trim().toLowerCase();
  const rows = today
    .filter((x) => filter === 'all' || x.cur === filter)
    .filter((x) => !mine || x.v.consultant === me.id)
    .filter((x) => !kw || `${x.v.cust.name} ${x.v.cust.code} ${x.v.cust.phone}`.toLowerCase().includes(kw))
    .sort((a, b) => FILTERS.indexOf(a.cur) - FILTERS.indexOf(b.cur) || (b.v.checkInAt ?? 0) - (a.v.checkInAt ?? 0));

  // Mở hồ sơ khách đúng bước đang làm (xong hết thì mở Kết quả)
  const open = (v: Visit, cur: CurStep) => {
    if (cur === 'wait') { navigate('/app-tuvan'); return; }
    const url = STEPS.find((s) => s.key === cur)?.url ?? '/ket-qua-tu-van';
    navigate(`${url}?v=${v.id}`);
  };

  type Row = { v: Visit; cur: CurStep };
  const columns: ColumnType<Row>[] = [
    {
      title: 'Khách', key: 'cust',
      render: (_, { v, cur }) => (
        <button type="button" onClick={() => open(v, cur)} className="text-left">
          <div className="font-semibold text-[#3e91ab] hover:underline">{v.cust.name}</div>
          <div className="text-xs text-gray-500">{v.cust.code} · {v.cust.phone}</div>
        </button>
      ),
    },
    { title: 'Check-in', key: 'in', width: 80, render: (_, { v }) => (v.checkInAt ? hm(v.checkInAt) : '—') },
    { title: 'Nguồn', key: 'src', render: (_, { v }) => <span className="flex flex-wrap items-center gap-1"><SourceTag v={v} />{v.owner && <span className="text-xs text-gray-500">{v.owner}</span>}</span> },
    { title: 'Tư vấn viên', key: 'tv', render: (_, { v }) => (v.consultant ? <span>{who(v.consultant)}{v.room ? <span className="text-xs text-gray-500"> · {v.room}</span> : null}</span> : <span className="text-gray-400">Chưa phân</span>) },
    {
      title: 'Đang ở bước', key: 'step',
      render: (_, { v, cur }) => <span className="flex items-center gap-2"><Dots v={v} cur={cur} /><span className="text-[13px] font-medium">{STEP_LABEL[cur]}</span></span>,
    },
    {
      title: 'Báo giá · đã thu', key: 'money', align: 'right',
      render: (_, { v }) => {
        const total = v.quote ? calc(v.quote).total : 0;
        return total ? <span className="tabular-nums"><b>{vnd(total)}</b><div className="text-xs text-gray-500">đã thu {vnd(paidOf(v))}</div></span> : <span className="text-gray-400">—</span>;
      },
    },
    { title: 'Trạng thái', key: 'st', render: (_, { v }) => <StatusTag v={v} /> },
    { title: '', key: 'act', width: 90, align: 'right', render: (_, { v, cur }) => <Button size="small" onClick={() => open(v, cur)}>{cur === 'wait' ? 'Nhận' : 'Mở'}</Button> },
  ];

  return (
    <Shell title="Khách đang tư vấn" sub="Mọi khách hôm nay theo bước đang làm — bấm khách để mở hồ sơ và làm tiếp đúng bước">
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
        {([['all', 'Tất cả', today.length], ...FILTERS.map((s) => [s, STEP_LABEL[s], count(s)])] as [CurStep | 'all', string, number][]).map(([f, label, n]) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`rounded-lg border px-1 py-2 text-center transition-colors ${filter === f ? 'border-[#3e91ab] bg-[#3e91ab]/10' : 'border-gray-200 bg-white hover:border-[#3e91ab] dark:border-gray-700 dark:bg-gray-800'}`}>
            <div className="text-lg font-bold leading-6 text-gray-800 dark:text-gray-100">{n}</div>
            <div className="truncate text-[11px] text-gray-500">{label}</div>
          </button>
        ))}
      </div>
      <Card
        title={filter === 'all' ? 'Tất cả khách hôm nay' : STEP_LABEL[filter]}
        extra={(
          <span className="flex flex-wrap items-center gap-3">
            <Input allowClear size="small" prefix={<Search size={13} className="text-gray-400" />} placeholder="Tên, mã khách, SĐT" value={q} onChange={(e) => setQ(e.target.value)} className="!w-[200px]" />
            <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"><Switch size="small" checked={mine} onChange={setMine} />Khách của tôi</span>
          </span>
        )}
      >
        <RTable rowKey={(r) => r.v.id} columns={doctor ? columns.filter((c) => c.key !== 'money') : columns} dataSource={rows} scrollX={980} empty="Không có khách ở bước này" />
      </Card>
    </Shell>
  );
}
